import type { ComponentProps, PropsWithChildren } from 'react';
import React, { useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { width, type WidthProps } from 'styled-system';

const SWIPE_THRESHOLD = 50;

interface SwipeHandlerProps extends PropsWithChildren {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onClick?: () => void;
}

const isSelectElement = (target: HTMLElement): boolean => target.tagName === 'SELECT';

const SwipeContainer = styled.div<WidthProps>`
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:active {
    cursor: grabbing;
  }

  *:not(select) {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  ${width};
`;

const swipeContainerStyles: ComponentProps<typeof SwipeContainer> = {
  width: 1,
};

// eslint-disable-next-line max-lines-per-function
const SwipeHandler = ({ onSwipeLeft, onSwipeRight, onClick, children }: SwipeHandlerProps) => {
  const startX = useRef<Nullable<number>>(null);
  const startY = useRef<Nullable<number>>(null);
  const isSwiping = useRef(false);
  const hasSwiped = useRef(false);

  const handleStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if ('button' in e && e.button !== 0) return;

    // Don't interfere with select elements
    if (isSelectElement(e.target as HTMLElement)) return;

    if ('touches' in e && e.touches.length > 0) {
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
    } else if ('clientX' in e) {
      startX.current = e.clientX;
      startY.current = e.clientY;

      // For mouse events, prevent default to avoid text selection
      if (e.cancelable) {
        e.preventDefault();
      }
    }

    isSwiping.current = true;
    hasSwiped.current = false;
  }, []);

  const handleMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isSwiping.current || startX.current === null) return;

    // For mouse events, make sure we only process if the button is pressed
    if ('buttons' in e && e.buttons !== 1) {
      handleCancel();

      return;
    }

    // Cancel swipe if multi-touch is detected (pinch gesture)
    if ('touches' in e && e.touches.length > 1) {
      handleCancel();

      return;
    }

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaX = startX.current - clientX;
    const deltaY = startY.current !== null ? Math.abs(startY.current - clientY) : 0;

    handleMovePreventDefault(deltaX, deltaY, e);
  }, []);

  const handleMovePreventDefault = useCallback(
    (deltaX: number, deltaY: number, e: React.TouchEvent | React.MouseEvent) => {
      // Prevent default for both touch and mouse events to ensure smooth swiping
      if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > deltaY) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    },
    []
  );

  const handleEnd = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isSwiping.current || startX.current === null) return;

      // Don't trigger swipe if there are still other fingers on screen (pinch gesture)
      if ('touches' in e && e.touches.length > 0) {
        handleCancel();

        return;
      }

      const endX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
      const deltaX = startX.current - endX;

      if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
        hasSwiped.current = true;
        if (deltaX > 0) {
          onSwipeLeft?.();
        } else {
          onSwipeRight?.();
        }
      }

      startX.current = null;
      startY.current = null;
      isSwiping.current = false;
    },
    [onSwipeLeft, onSwipeRight]
  );

  const handleCancel = useCallback(() => {
    startX.current = null;
    startY.current = null;
    isSwiping.current = false;
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't interfere with select elements
      if (isSelectElement(e.target as HTMLElement)) return;

      if (hasSwiped.current) {
        e.stopPropagation();
        e.preventDefault();

        return;
      }
      onClick?.();
    },
    [onClick]
  );

  return (
    <SwipeContainer
      {...swipeContainerStyles}
      data-testid="swipe-handler"
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onTouchCancel={handleCancel}
      onClickCapture={handleClick}
    >
      {children}
    </SwipeContainer>
  );
};

export default SwipeHandler;
